import { memo } from 'react';
import { NODE_PAD } from '@/layout/constants';
import type { LaidOutNode } from '@/layout/types';
import { StageIconPaths } from '../StageIcon';
import { formatCount, formatPercent } from '@/lib/format';

interface Props {
  node: LaidOutNode;
  dimmed: boolean;
  selected: boolean;
  onActivate: (node: LaidOutNode) => void;
  onHover: (node: LaidOutNode | null, event?: React.MouseEvent | React.FocusEvent) => void;
}

/**
 * One card. Text is laid out by tier so a small card never overflows: `full`
 * gets title + detail + count + share, `mid` drops the detail, `compact` keeps
 * only title and count.
 */
export const FlowNode = memo(function FlowNode({
  node,
  dimmed,
  selected,
  onActivate,
  onHover,
}: Props) {
  const x = node.x + NODE_PAD;
  const label =
    `${node.title}: ${formatCount(node.display)} people, ` +
    `${formatPercent(node.share)} ${node.shareLabel}.` +
    (node.explode ? ' Activate to explode this step.' : ' Activate to edit this stage.');

  return (
    <g
      className={[
        'flow-node',
        `tone-${node.tone}`,
        node.tier === 'compact' ? 'is-compact' : '',
        selected ? 'is-selected' : '',
        dimmed ? 'is-dimmed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={() => onActivate(node)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onActivate(node);
      }}
      onMouseEnter={(event) => onHover(node, event)}
      onMouseMove={(event) => onHover(node, event)}
      onMouseLeave={() => onHover(null)}
      onFocus={(event) => onHover(node, event)}
      onBlur={() => onHover(null)}
    >
      <rect className="node-rect" x={node.x} y={node.y} width={node.w} height={node.h} rx={8} />

      {/* Points at the drawer stage that edits this card. Sits in the bottom
          corner rather than beside the title, which a long title would run into. */}
      {node.stage && node.tier !== 'compact' && (
        <g
          className="node-icon"
          transform={`translate(${node.x + node.w - 27}, ${node.y + node.h - 26})`}
          aria-hidden="true"
        >
          <StageIconPaths stage={node.stage} />
        </g>
      )}

      {node.tier === 'full' && (
        <>
          <text className="node-title" x={x} y={node.y + 21}>
            {node.title}
          </text>
          {node.detail && (
            <text className="node-detail" x={x} y={node.y + 38}>
              {node.detail}
            </text>
          )}
          <text className="node-count" x={x} y={node.y + 68}>
            {formatCount(node.display)}
          </text>
          <text className="node-share" x={x} y={node.y + 86}>
            {formatPercent(node.share)} {node.shareLabel}
          </text>
        </>
      )}

      {node.tier === 'mid' && (
        <>
          <text className="node-title" x={x} y={node.y + 20}>
            {node.title}
          </text>
          <text className="node-count" x={x} y={node.y + 46}>
            {formatCount(node.display)}
          </text>
          <text className="node-share" x={x} y={node.y + 62}>
            {formatPercent(node.share)} {node.shareLabel}
          </text>
        </>
      )}

      {node.tier === 'compact' && (
        <>
          <text className="node-title" x={x} y={node.y + 17}>
            {node.title}
          </text>
          <text className="node-count" x={x} y={node.y + 39}>
            {formatCount(node.display)}
          </text>
        </>
      )}
    </g>
  );
});
